import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, WritableSignal, inject, input, signal } from '@angular/core'; // Added WritableSignal
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxTtsService } from '../services/ngx-tts.service';
import { AudioSpeed, TTSRequest } from '../models/models';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SliderModule } from 'primeng/slider';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext'; // Import InputTextModule
import { VoiceSelectorComponent } from './voice-selector/voice-selector.component';
import { OnActionEvent } from '@dataclouder/ngx-core';

import { CloudStorageData, MultiObjectStorageService, ObjectStorageData } from '@dataclouder/ngx-cloud-storage';

export interface TTSGenerated {
  transcription: any;
  text: string;
  voice: string;
  generateTranscription: boolean;
  speedRate: number;
  speed: AudioSpeed;
  storage?: CloudStorageData; // Optional path for storage
}

@Component({
  selector: 'lib-ngx-tts',
  templateUrl: './ngx-tts.component.html',
  styleUrls: ['./ngx-tts.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    VoiceSelectorComponent,
    DialogModule,
    ButtonModule,
    SliderModule,
    SelectModule,
    InputTextModule, // Add InputTextModule here
  ],
})
export class NgxTtsComponent implements OnInit {
  @Output() ttsGenerated = new EventEmitter<TTSGenerated>();
  // Use @Input for the initial value and a separate signal for internal state
  @Input() set path(value: string) {
    this.internalPath.set(value);
  }
  internalPath: WritableSignal<string> = signal(''); // Signal to manage the path internally

  private ttsService = inject(NgxTtsService);
  private cdr = inject(ChangeDetectorRef);
  private storageService = inject(MultiObjectStorageService);

  // Using regular variable instead of signal for p-dialog compatibility
  private _isVoicesVisible = false;

  // Getter for isVoicesVisible
  public get isVoicesVisible(): boolean {
    return this._isVoicesVisible;
  }

  // Setter for isVoicesVisible
  public set isVoicesVisible(value: boolean) {
    this._isVoicesVisible = value;
  }

  // UI state properties
  public message = signal<string>('');
  public selectedVoice = signal<string>('default');
  public speedRate = signal<number>(1.0); // Signal for slider value (0-2 range)
  public selectedSpeed = signal<AudioSpeed>(AudioSpeed.Regular); // Re-add signal for dropdown
  public isGenerating = signal<boolean>(false);
  public audioUrl = signal<string | null>(null);

  // Available options
  public voices = [
    { id: 'default', name: 'Default' },
    { id: 'male', name: 'Male' },
    { id: 'female', name: 'Female' },
  ];

  // Re-add options for p-select based on AudioSpeed enum
  public speedOptions = Object.values(AudioSpeed).map((speed) => ({ label: this.formatSpeedLabel(speed), value: speed }));

  constructor() {}

  async ngOnInit() {
    // this.voices.push(...voices.map((voice) => ({ id: voice.name, name: voice.name })));
  }

  /**
   * Synthesizes the given text into speech and plays it.
   */
  async generateAudio(): Promise<void> {
    const text = this.message();
    if (!text) {
      console.warn('No text provided to speak.');
      return;
    }

    try {
      this.isGenerating.set(true);

      // Create TTSRequest object with the necessary parameters
      const ttsRequest: TTSRequest = {
        text: text,
        voice: this.selectedVoice(),
        generateTranscription: true,
        speedRate: this.speedRate(),
        speed: this.selectedSpeed(),
      };

      const audioData = await this.ttsService.getTextAudioFile(ttsRequest);

      let storageData: ObjectStorageData | null = null;

      if (this.internalPath()) {
        storageData = await this.storageService.uploadObject(audioData.blob, this.internalPath());
        console.log('Storage data:', storageData);
      }

      // Prepare the event payload
      const generatedEvent: TTSGenerated = {
        // storage: audioData.storage, // Removed as 'storage' is not in AudioGenerated
        transcription: audioData.transcription,
        text: ttsRequest.text,
        voice: ttsRequest.voice,
        generateTranscription: ttsRequest.generateTranscription,
        speedRate: ttsRequest.speedRate,
        speed: ttsRequest.speed,
        storage: storageData, // Include path in the event
      };

      // Emit the event
      this.ttsGenerated.emit(generatedEvent);

      // Handle audio playback
      if (audioData.blobUrl) {
        this.audioUrl.set(audioData.blobUrl);
        this.playAudio(); // Keep playing audio immediately if needed, or let the consumer decide based on the event
      }

      // Log transcription if available (already included in the event)
      if (audioData.transcription) {
        console.log('Transcription:', audioData.transcription); // Keep logging for debugging if desired
      }
    } catch (error) {
      console.error('TTS synthesis failed:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Play the generated audio
   */
  playAudio(): void {
    const url = this.audioUrl();
    if (url) {
      const audio = new Audio(url);
      audio.play();
    }
  }

  /**
   * Set the selected voice
   */
  setVoice(voice: string): void {
    this.selectedVoice.set(voice);
  }

  /**
   * Helper function to format speed labels (optional, for better display) - Re-added
   */
  private formatSpeedLabel(speed: AudioSpeed): string {
    switch (speed) {
      case AudioSpeed.VerySlow:
        return 'Very Slow';
      case AudioSpeed.Slow:
        return 'Slow';
      case AudioSpeed.Regular:
        return 'Regular';
      case AudioSpeed.Fast:
        return 'Fast';
      case AudioSpeed.VeryFast:
        return 'Very Fast';
      default:
        return speed;
    }
  }

  /**
   * Clean up resources when component is destroyed
   */
  ngOnDestroy(): void {
    const url = this.audioUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  public selectVoice(voice: OnActionEvent): void {
    this.selectedVoice.set(voice.item.name);
    this.isVoicesVisible = false;
    this.cdr.markForCheck();
  }
}
